<template>
  <footer id="footer" ref="footerRef" class="footer">
    <div class="footer__main">
      <!-- Left Content -->
      <section class="footer__content">
        <div class="footer__cta-content">
          <h2 ref="titleRef" class="footer__title">
            <span class="footer__title-line">{{ $t('common.footer.cta.title1') }}</span>
            <span class="footer__title-line">{{ $t('common.footer.cta.title2') }}</span>
            <span class="footer__title-accent">{{ $t('common.footer.cta.titleAccent') }}</span>
          </h2>
          <p ref="descriptionRef" class="footer__description">
            {{ $t('common.footer.cta.description') }}
          </p>
        </div>

        <!-- Social Links Vertical -->
        <div class="footer__contact">
          <a
            href="https://instagram.com/juanlias_"
            target="_blank"
            rel="noopener noreferrer"
            class="footer__social-link"
          >
            Instagram
          </a>
          <a
            href="https://x.com/juanlias_"
            target="_blank"
            rel="noopener noreferrer"
            class="footer__social-link"
            v-if="false"
          >
            X (Twitter)
          </a>
          <a
            href="https://linkedin.com/in/juanlias"
            target="_blank"
            rel="noopener noreferrer"
            class="footer__social-link"
          >
            LinkedIn
          </a>
          <a
            href="mailto:contact@juanlias.com"
            class="footer__social-link footer__social-link--email"
          >
            contact@juanlias.com
          </a>
        </div>

        <!-- Copyright -->
        <p class="footer__copyright">&copy; {{ currentYear }} {{ $t('common.footer.rights') }} <br>
          <span class="footer__version">v0.1.0</span>
        </p>
      </section>

      <!-- Right Image -->
      <div class="footer__media">
        <img :src="footerImage" alt="Juan Lias" class="footer__image" />
      </div>
    </div>

    <!-- Decoración de fondo -->
    <div class="footer__decoration footer__decoration--left"></div>
    <div class="footer__decoration footer__decoration--right"></div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useTheme } from '@/composables/useTheme'
import footerImageDark from '@/assets/images/juan-lias-3.webp'
import footerImageLight from '@/assets/images/juan-lias-3-light.webp'

// Obtener estado del tema
const { isDark } = useTheme()

// Imagen del footer reactiva según el tema
const footerImage = computed(() => (isDark.value ? footerImageDark : footerImageLight))

const currentYear = computed(() => new Date().getFullYear())
</script>

<style scoped>
/* ==========================================================================
   FOOTER - Estructura principal
   ========================================================================== */
.footer {
  @apply relative overflow-hidden lg:h-screen;
  background-color: var(--color-deep-black);
}

.footer__main {
  @apply grid grid-cols-1 lg:grid-cols-2;
  height: 100%;
}

/* ==========================================================================
   LEFT CONTENT
   ========================================================================== */
.footer__content {
  @apply flex flex-col justify-between px-8 lg:px-16 py-12 lg:py-16 !pb-10;
  height: 100%;
  overflow: hidden;
}

.footer__cta-content {
  @apply flex flex-col gap-6 pt-8 lg:pt-20;
}

.footer__title {
  @apply flex flex-col gap-0 leading-none;
}

.footer__title-line {
  @apply font-jakarta text-text-primary font-bold text-4xl  lg:text-4xl tracking-tight;
}

.footer__title-accent {
  @apply font-madi font-normal text-5xl sm:text-6xl xl:text-9xl text-electric-purple;
}

.footer__description {
  @apply text-base leading-relaxed text-text-secondary max-w-sm;
}

/* ==========================================================================
   CONTACT / SOCIAL LINKS
   ========================================================================== */
.footer__contact {
  @apply flex flex-col items-start gap-2 mt-8 lg:mt-0;
}

.footer__social-link {
  @apply text-text-primary font-medium text-base lg:text-lg;
  @apply transition-all duration-300;
  @apply hover:text-electric-purple;
  position: relative;
}

.footer__social-link::after {
  content: '';
  @apply absolute bottom-0 left-0 w-0 h-[1px] bg-electric-purple;
  @apply transition-all duration-300;
}

.footer__social-link:hover::after {
  @apply w-full;
}

/* ==========================================================================
   COPYRIGHT
   ========================================================================== */
.footer__copyright {
  @apply text-sm text-text-secondary mt-8 lg:mt-0;
}

/* ==========================================================================
   RIGHT IMAGE
   ========================================================================== */
.footer__media {
  @apply relative overflow-hidden flex items-end justify-center lg:h-full p-10 lg:pr-0;
}

.footer__image {
  @apply w-full h-full object-cover lg:w-auto lg:object-contain lg:object-bottom lg:max-w-full rounded-[2rem];
  object-position: center 20%;
  corner-shape: squircle;
}

@screen lg {
  .footer__image {
    object-position: bottom;
  }
}

/* ==========================================================================
   DECORACIONES
   ========================================================================== */
.footer__decoration {
  @apply absolute rounded-full pointer-events-none;
  filter: blur(80px);
  will-change: transform;
}

.footer__decoration--left {
  @apply -bottom-20 -left-20 w-80 h-80;
  background-color: var(--color-electric-purple);
  opacity: 0;
}

.footer__decoration--right {
  @apply -bottom-20 -right-20 w-60 h-60;
  background-color: var(--color-electric-purple);
  opacity: 0;
}

.footer__version {
  @apply text-xs text-[var(--color-text-muted)];
}
</style>
